// Helfer für Fälligkeitsdaten und Presets im TaskFormModal

export const TASK_TYPES = ["todo", "call", "email", "meeting"];

export const PRIORITIES = [
  { value: "", label: "None" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const DUE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "in_2_business_days", label: "In 2 business days" },
  { value: "in_3_business_days", label: "In 3 business days" },
  { value: "in_1_week", label: "In 1 week" },
  { value: "in_2_weeks", label: "In 2 weeks" },
  { value: "in_1_month", label: "In 1 month" },
  { value: "in_3_months", label: "In 3 months" },
  { value: "in_6_months", label: "In 6 months" },
  { value: "in_1_year", label: "In 1 year" },
  { value: "custom", label: "Custom date" },
];

// Vorlaufzeiten fuer die Faelligkeits-Erinnerung. Minuten vor `dueDate`,
// 0 = punktgenau. Der Scheduler laeuft jede Minute — feiner als das wird es nicht.
// Der Lambda-Lauf holt sein Zeitfenster am groessten Wert hier (LOOKAHEAD_MS in
// amplify/backend/function/lineupdashboardCrmMailSyncScheduled/src/taskReminders.js) —
// wer eine laengere Vorlaufzeit ergaenzt, muss dort mitziehen.
export const REMINDER_OFFSETS = [
  { value: 0, label: "At the due time" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
  { value: 1440, label: "1 day before" },
];

/**
 * Addiert Werktage (Montag - Freitag) zu einem Startdatum.
 * Samstag und Sonntag werden übersprungen. Negative Werte zaehlen rueckwaerts
 * (Vorziehen in der Mehrfachauswahl der Task-Queue).
 */
export const addBusinessDays = (startDate, days) => {
  let date = new Date(startDate);
  const step = days < 0 ? -1 : 1;
  let added = 0;
  while (added < Math.abs(days)) {
    date.setDate(date.getDate() + step);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // Nicht Sonntag (0) oder Samstag (6)
      added++;
    }
  }
  return date;
};

/**
 * Berechnet das Zieldatum basierend auf dem gewählten Preset.
 */
export const calculatePresetDate = (preset, fromDate = new Date()) => {
  const base = new Date(fromDate);
  switch (preset) {
    case "today":
      return base;
    case "tomorrow": {
      const d = new Date(base);
      d.setDate(d.getDate() + 1);
      return d;
    }
    case "in_2_business_days":
      return addBusinessDays(base, 2);
    case "in_3_business_days":
      return addBusinessDays(base, 3);
    case "in_1_week": {
      const d = new Date(base);
      d.setDate(d.getDate() + 7);
      return d;
    }
    case "in_2_weeks": {
      const d = new Date(base);
      d.setDate(d.getDate() + 14);
      return d;
    }
    case "in_1_month": {
      const d = new Date(base);
      d.setMonth(d.getMonth() + 1);
      return d;
    }
    case "in_3_months": {
      const d = new Date(base);
      d.setMonth(d.getMonth() + 3);
      return d;
    }
    case "in_6_months": {
      const d = new Date(base);
      d.setMonth(d.getMonth() + 6);
      return d;
    }
    case "in_1_year": {
      const d = new Date(base);
      d.setFullYear(d.getFullYear() + 1);
      return d;
    }
    default:
      return base;
  }
};

/**
 * Formatiert ein Datum in lokales YYYY-MM-DD (für <input type="date">)
 */
export const formatDateLocal = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Faelligkeitszustand einer Aufgabe. LOKAL gerechnet, weil daneben der lokale Tag
 * steht (dateFormatter): gegen den UTC-Tag verglichen stand bei einem Nutzer
 * oestlich von UTC "ueberfaellig" an einer Aufgabe, deren Datum den heutigen Tag
 * zeigte — in China trifft das jede Faelligkeit vor 08:00 Ortszeit, in Berlin die
 * zwischen 00:00 und 02:00.
 *
 * ponytail: `new Date` auf einen Wert OHNE Uhrzeit ("2026-08-31", so kommen aeltere
 * HubSpot-Aufgaben) liest UTC-Mitternacht. Bei negativem Offset (Amerika) waere das
 * der Vortag; alle Nutzer sitzen in UTC+X. Ceiling: kommt eine Zeitzone westlich von
 * UTC dazu, muss ein Wert ohne Uhrzeit als reines Tagespraefix behandelt werden.
 */
export const dueState = (dueDate) => {
  if (!dueDate) return "none";
  const today = formatDateLocal(new Date());
  const due = formatDateLocal(dueDate);
  if (due < today) return "overdue";
  if (due === today) return "today";
  return "upcoming";
};

// Anfang und Ende eines LOKAL gewaehlten Tages als ISO-Zeitpunkt — fuer einen
// `between`-Filter auf einem ISO-Feld. Mit `${day}T00:00:00.000Z` gebaut waere das
// Fenster in China von 08:00 bis 07:59 des Folgetages gelaufen.
export const localDayStartIso = (day) => new Date(`${day}T00:00:00`).toISOString();
export const localDayEndIso = (day) => new Date(`${day}T23:59:59.999`).toISOString();

// Die vier Zeitraeume des Aufgaben-Panels auf dem Dashboard, als LOKALE Tagesgrenzen
// (YYYY-MM-DD) — genau die Form, die der dueDate-Filter der /tasks-Seite erwartet,
// damit Kachel und geoeffnete Liste dieselbe Menge zeigen. null = offenes Ende.
export const dueRangeBounds = (range) => {
  const day = (offset) => {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return formatDateLocal(date);
  };
  switch (range) {
    case "today":
      return { from: day(0), to: day(0) };
    case "overdue":
      return { from: null, to: day(-1) };
    case "tomorrow":
      return { from: day(1), to: day(1) };
    default:
      return { from: null, to: null };
  }
};

// Dieselbe Regel, die /tasks serverseitig als `between` anwendet: eine Aufgabe OHNE
// Faelligkeit faellt aus jedem gesetzten Zeitraum heraus, nur "alle" behaelt sie.
export const inDueRange = (task, bounds) => {
  if (!bounds?.from && !bounds?.to) return true;
  if (!task?.dueDate) return false;
  if (bounds.from && task.dueDate < localDayStartIso(bounds.from)) return false;
  if (bounds.to && task.dueDate > localDayEndIso(bounds.to)) return false;
  return true;
};

/**
 * Kombiniert ein Datum (YYYY-MM-DD) und eine Uhrzeit (HH:mm) zu einem ISO-String
 */
export const combineDateAndTimeToIso = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = (timeStr || "09:00").split(":").map(Number);
  const d = new Date(year, month - 1, day, hours || 0, minutes || 0, 0, 0);
  return d.toISOString();
};

/**
 * Umkehrung von combineDateAndTimeToIso: lokale Uhrzeit HH:mm aus einem ISO-String
 * (für <input type="time"> beim Bearbeiten einer bestehenden Aufgabe).
 */
export const formatTimeLocal = (isoString) => {
  if (!isoString) return "09:00";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "09:00";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/**
 * Berechnet das Fälligkeitsdatum für einen Follow-Up Task:
 * - Minuten / Stunden: minutengenau / stundengenau ab baseDate
 * - Werktage (business_days): addBusinessDays, feste Uhrzeit 09:00 Ortszeit
 * - Wochen (weeks): X Wochen (Werktag garantiert), feste Uhrzeit 09:00 Ortszeit
 */
export const calculateFollowUpDate = (amount, unit = "business_days", baseDate = new Date()) => {
  const num = Math.max(1, Number(amount) || 1);
  const base = new Date(baseDate);

  switch (unit) {
    case "minutes":
      return new Date(base.getTime() + num * 60 * 1000);

    case "hours":
      return new Date(base.getTime() + num * 60 * 60 * 1000);

    case "business_days": {
      const d = addBusinessDays(base, num);
      d.setHours(9, 0, 0, 0);
      return d;
    }

    case "weeks": {
      const d = new Date(base);
      d.setDate(d.getDate() + num * 7);
      if (d.getDay() === 6) {
        d.setDate(d.getDate() + 2); // Samstag -> Montag
      } else if (d.getDay() === 0) {
        d.setDate(d.getDate() + 1); // Sonntag -> Montag
      }
      d.setHours(9, 0, 0, 0);
      return d;
    }

    default: {
      const d = addBusinessDays(base, num);
      d.setHours(9, 0, 0, 0);
      return d;
    }
  }
};

/**
 * Formatiert eine Live-Vorschau für das berechnete Follow-Up Datum
 */
export const formatFollowUpPreview = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const dateStr = date.toLocaleDateString([], {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timeStr = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${dateStr}, ${timeStr}`;
};
